"use client";

import { useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import MapView from "./map-view";
import {
  ACTIVE_STATUSES,
  acceptOpenRequest,
  partnerCancelAcceptance,
  distanceKmBetween,
  setPartnerOnline,
  shareMyContact,
  startLocationPublisher,
  updateJobStatus,
  watchContacts,
  watchLiveLocations,
  watchMyAssignedRequest,
  watchOpenRequests,
  markPaymentCollected,
  revertPaymentCollected,
  watchPartnerProfile,
  type ContactCard,
  type Coordinates,
  type OpenRequest,
  type PartnerProfile,
} from "./dispatch-client";
import { watchMyPartnerApplication, type PartnerApplicationRecord } from "./admin";
import { sendRequestMessage, watchRequestMessages } from "./firebase-data";
import { explainQuote, suggestQuote, timeSlotFor } from "./pricing";
import { summarizeEarnings, watchMyJobHistory, watchMyCommissions, claimCommissionPaid, type PartnerJob, type CommissionDue } from "./partner-earnings";
import { armDispatchAlerts, disarmDispatchAlerts, ensureDispatchPermission, markUserGestured, startDispatchRing, stopDispatchRing } from "./dispatch-alerts";
import { COMMISSION_RATE, PLATFORM_UPI, platformUpiLink } from "./billing";
import { nextJobStep, stepNeedsJobCode } from "./job-security";
import JobStepGuide from "./job-step-guide";
import { liveReach, partnerGuide } from "./job-flow";

const STATUS_LABELS: Record<string, string> = {
  completed: "Paid",
  cancelled: "Cancelled",
  broadcasting: "Left",
  partner_accepted: "Awaiting price OK",
  confirmed: "Confirmed",
  en_route: "On the way",
  arrived: "Arrived",
  in_progress: "Working",
};

type PartnerDashboardProps = {
  user: User | null;
  onRequireSignIn: () => void;
  onToast: (message: string) => void;
  onExit: () => void;
};

function minutesAgo(ms: number) {
  const minutes = Math.max(0, Math.round((Date.now() - ms) / 60000));
  if (minutes < 1) return "just now";
  if (minutes === 1) return "1 min ago";
  return `${minutes} min ago`;
}

type AssignedJob = (OpenRequest & {
  status: string;
  price?: number;
  paymentMethod?: string;
  paymentStatus?: string;
  jobCode?: string;
}) | null;

const JOB_STEPS: Array<{ status: "en_route" | "arrived" | "in_progress" | "completed"; label: string }> = [
  { status: "en_route", label: "On the way" },
  { status: "arrived", label: "Arrived" },
  { status: "in_progress", label: "Start work" },
  { status: "completed", label: "Job done" },
];

export default function PartnerDashboard({ user, onRequireSignIn, onToast, onExit }: PartnerDashboardProps) {
  const [profile, setProfile] = useState<PartnerProfile | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [application, setApplication] = useState<PartnerApplicationRecord | null>(null);
  const [myLocation, setMyLocation] = useState<Coordinates | null>(null);
  const [openRequests, setOpenRequests] = useState<OpenRequest[]>([]);
  const [quotes, setQuotes] = useState<Record<string, string>>({});
  const [now, setNow] = useState(() => new Date());
  const [history, setHistory] = useState<PartnerJob[]>([]);
  const [commissions, setCommissions] = useState<CommissionDue[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [skipped, setSkipped] = useState<Record<string, true>>({});
  const [job, setJob] = useState<AssignedJob>(null);
  const [contacts, setContacts] = useState<Record<string, ContactCard>>({});
  const [customerPoint, setCustomerPoint] = useState<Coordinates | null>(null);
  const [phoneForm, setPhoneForm] = useState("");
  const [chatText, setChatText] = useState("");
  const [chatMessages, setChatMessages] = useState<Array<{ mine: boolean; text: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [muted, setMuted] = useState(false);
  const [notifyDenied, setNotifyDenied] = useState(false);
  const [verifyStep, setVerifyStep] = useState<"arrived" | "in_progress" | null>(null);
  const [verifyCode, setVerifyCode] = useState("");
  const stopPublisherRef = useRef<(() => void) | null>(null);
  const locationRef = useRef<Coordinates | null>(null);

  const signedIn = !!user && !user.isAnonymous;
  locationRef.current = myLocation;

  // Re-price on the minute so a quote crossing into peak or late-night hours updates itself.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!signedIn || !user) return;
    return watchPartnerProfile(user.uid, (next) => {
      setProfile(next);
      setProfileLoaded(true);
    });
  }, [signedIn, user]);

  useEffect(() => {
    if (!signedIn) return;
    return watchMyPartnerApplication(setApplication);
  }, [signedIn]);

  // Keep own GPS fresh while dashboard is open.
  useEffect(() => {
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (position) => setMyLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
      () => undefined,
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  useEffect(() => {
    if (!myLocation) return;
    setOpenRequests((prev) => {
      const next = prev
        .map((row) => ({
          ...row,
          distanceKm: Math.round(distanceKmBetween(myLocation, row.coordinates) * 10) / 10,
        }))
        .filter((row) => row.distanceKm <= 25)
        .sort((left, right) => (left.distanceKm ?? 999) - (right.distanceKm ?? 999));
      const same = next.length === prev.length && next.every((row, index) => row.id === prev[index]?.id && row.distanceKm === prev[index]?.distanceKm);
      return same ? prev : next;
    });
  }, [myLocation]);

  useEffect(() => {
    if (!signedIn || !profile?.online || job) {
      setOpenRequests([]);
      stopDispatchRing();
      return;
    }
    let knownIds = new Set<string>();
    let primed = false;
    return watchOpenRequests(() => locationRef.current, 25, (rows) => {
      if (primed) {
        rows.forEach((request) => {
          if (knownIds.has(request.id)) return;
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            new Notification("New roadside SOS", {
              body: `${request.problem} · ${request.vehicle} · ${request.distanceKm ?? "?"} km`,
              tag: request.id,
              requireInteraction: true,
            });
          }
        });
      }
      knownIds = new Set(rows.map((row) => row.id));
      primed = true;
      setOpenRequests(rows);
    }, user?.uid);
  }, [signedIn, profile?.online, job, user?.uid]);

  useEffect(() => {
    if (!signedIn) return;
    return watchMyAssignedRequest(setJob);
  }, [signedIn]);

  useEffect(() => {
    if (!signedIn) {
      setHistory([]);
      return;
    }
    return watchMyJobHistory(setHistory);
  }, [signedIn]);

  useEffect(() => {
    if (!signedIn) {
      setCommissions([]);
      return;
    }
    return watchMyCommissions(setCommissions);
  }, [signedIn]);

  // Publish live GPS while a job is active (and briefly after cancellation to prove good faith).
  useEffect(() => {
    if (!job || !["partner_accepted", ...ACTIVE_STATUSES, "cancelled"].includes(job.status)) {
      stopPublisherRef.current?.();
      stopPublisherRef.current = null;
      return;
    }
    stopPublisherRef.current?.();
    stopPublisherRef.current = startLocationPublisher(job.id, "partner");
    return () => {
      stopPublisherRef.current?.();
      stopPublisherRef.current = null;
    };
  }, [job?.id, job?.status]);

  useEffect(() => {
    if (!job) {
      setContacts({});
      setCustomerPoint(null);
      setChatMessages([]);
      return;
    }
    const jobLive = ACTIVE_STATUSES.includes(job.status);
    const stopContacts = jobLive ? watchContacts(job.id, setContacts) : () => undefined;
    const stopLocations = watchLiveLocations(job.id, (points) => {
      const customer = Object.entries(points).find(([, point]) => point.role === "customer");
      setCustomerPoint(customer ? { latitude: customer[1].latitude, longitude: customer[1].longitude } : job.coordinates);
    });
    const stopMessages = watchRequestMessages(job.id, (rows) => {
      setChatMessages(rows.map((row) => ({ mine: row.senderId === user?.uid, text: row.text })));
    });
    setCustomerPoint(job.coordinates);
    return () => {
      stopContacts();
      stopLocations();
      stopMessages();
    };
  }, [job?.id, job?.status, job?.coordinates, user?.uid]);

  const toggleOnline = async () => {
    if (!profile) return;
    if (!profile.online && !myLocation) {
      onToast("Allow location access — nearby matching needs your position");
      return;
    }
    try {
      if (!profile.online) {
        markUserGestured();
        const permission = await ensureDispatchPermission();
        setNotifyDenied(permission === "denied");
        await armDispatchAlerts();
      } else {
        disarmDispatchAlerts();
      }
      await setPartnerOnline(!profile.online);
      onToast(profile.online ? "You are offline — alerts stopped" : "You are ONLINE — keep this screen open. New SOS will ring until you accept or skip.");
    } catch {
      onToast("Could not update your status");
    }
  };

  const accept = async (request: OpenRequest) => {
    if (!myLocation) {
      onToast("Allow location access first");
      return;
    }
    setBusy(true);
    try {
      const suggested = suggestQuote({
        basePrice: profile?.basePrice ?? 299,
        distanceKm: request.distanceKm,
      });
      const quote = Number(quotes[request.id]) || suggested.total;
      await acceptOpenRequest(request.id, quote, myLocation);
      onToast(`Request locked at ₹${quote} — wait for the customer to agree`);
    } catch (error) {
      onToast(error instanceof Error ? error.message : "Could not accept — it may already be taken");
    } finally {
      setBusy(false);
    }
  };

  const tapJobStep = (status: "en_route" | "arrived" | "in_progress" | "completed") => {
    if (!job) return;
    const next = nextJobStep(job.status);
    if (next !== status && job.status !== status) {
      onToast(`Complete "${job.status.replace(/_/g, " ")}" first`);
      return;
    }
    if (stepNeedsJobCode(status) && job.jobCode) {
      setVerifyStep(status);
      setVerifyCode("");
      return;
    }
    if (status === "completed" && !window.confirm("Mark this job as done? Customer should have paid you first.")) return;
    void setStatus(status);
  };

  const submitVerifyCode = async () => {
    if (!job || !verifyStep) return;
    try {
      await setStatus(verifyStep, verifyCode.trim());
      setVerifyStep(null);
      setVerifyCode("");
    } catch (error) {
      onToast(error instanceof Error ? error.message : "Code did not match");
    }
  };

  const setStatus = async (status: "en_route" | "arrived" | "in_progress" | "completed", jobCode?: string) => {
    if (!job) return;
    try {
      await updateJobStatus(job.id, status, jobCode);
      if (status === "completed") onToast("Job done — collect the agreed amount in cash or UPI now");
      else if (status === "arrived") onToast("Arrival verified with customer code");
      else if (status === "in_progress") onToast("Work started — verified with customer code");
    } catch (error) {
      onToast(error instanceof Error ? error.message : "Status update failed");
      throw error;
    }
  };

  const markPaid = async () => {
    if (!job) return;
    if (!window.confirm("Confirm you received ₹" + (job.price || 0) + " from the customer?")) return;
    try {
      await markPaymentCollected(job.id);
      onToast("Marked as collected");
    } catch {
      onToast("Could not update payment");
    }
  };

  const undoPaid = async () => {
    if (!job) return;
    if (!window.confirm("Undo payment mark? Use this if you tapped by mistake.")) return;
    try {
      await revertPaymentCollected(job.id);
      onToast("Payment mark removed");
    } catch {
      onToast("Could not undo payment");
    }
  };

  const sharePhone = async () => {
    if (!job) return;
    try {
      await shareMyContact(job.id, phoneForm, profile?.name || "Mistri");
      onToast("Your number is now visible to the customer");
    } catch (error) {
      onToast(error instanceof Error ? error.message : "Number can be shared only after the customer confirms");
    }
  };

  const sendChat = async () => {
    if (!job || !chatText.trim()) return;
    try {
      await sendRequestMessage(job.id, chatText.trim());
      setChatText("");
    } catch (error) {
      onToast(error instanceof Error ? error.message : "Message blocked");
    }
  };

  const earnings = summarizeEarnings(history, now);
  const feesDue = commissions.filter((row) => row.status === "due" || row.status === "claimed");
  const feesDueTotal = feesDue.reduce((sum, row) => sum + row.amount, 0);
  const visibleRequests = openRequests.filter((request) => !skipped[request.id]);
  const alertKey = `${visibleRequests.length}:${visibleRequests[0]?.id || ""}`;
  const customerContact = job ? contacts[job.customerId] : undefined;
  const jobConfirmed = !!job && ACTIVE_STATUSES.includes(job.status);
  const partnerReach = liveReach(myLocation, customerPoint);
  const partnerFlow = job
    ? partnerGuide(job.status, job.paymentStatus === "collected", !!job.jobCode)
    : null;
  const nextStep = job ? nextJobStep(job.status) : null;

  useEffect(() => {
    if (!profile?.online || job || muted || !visibleRequests.length) {
      stopDispatchRing();
      return;
    }
    const top = visibleRequests[0];
    startDispatchRing(
      visibleRequests.length,
      `${top.problem} · ${top.vehicle} · ${top.distanceKm ?? "?"} km · pick any request`,
    );
  }, [profile?.online, job, muted, alertKey]);

  useEffect(() => () => disarmDispatchAlerts(), []);

  return (
    <section className="partner-dash">
      <div className="dash-head">
        <button type="button" className="brand brand-home" onClick={onExit} aria-label="Back to Mistry Bhai home">
          <span className="brand-mark"><span>MB</span></span>
          <span>Mistry <b>Bhai</b></span>
        </button>
        <div className="dash-head-meta">
          <span className="section-kicker">MISTRI DASHBOARD</span>
          <h2>{profile ? profile.name.split(" ")[0] : "Partner"}</h2>
        </div>
      </div>

      {!signedIn && (
        <div className="dash-card">
          <h3>Sign in to start earning</h3>
          <p>Your mistri profile, jobs and payouts are tied to your account.</p>
          <button className="primary" onClick={onRequireSignIn}>Sign in / create account <span>→</span></button>
        </div>
      )}

      {signedIn && profileLoaded && !profile && (
        <div className="dash-card">
          <h3>
            {application?.verificationStatus === "pending_review" && "Verification in progress"}
            {(application?.verificationStatus === "approved" || application?.verificationStatus === "auto_approved") && "Profile ready"}
            {application?.verificationStatus === "rejected" && "Application needs fixes"}
            {!application && "Submit partner KYC first"}
          </h3>
          <p>
            {!application && "Use Become a partner on the home screen to submit full KYC with camera selfie + documents. Passing checks auto-approves you."}
            {application?.verificationStatus === "pending_review" && "Thanks — review is still pending."}
            {application?.verificationStatus === "rejected" && `Rejected: ${application.rejectionReason || "Please resubmit with clearer documents."}`}
            {(application?.verificationStatus === "approved" || application?.verificationStatus === "auto_approved") && "You are verified. Refresh if your live profile is still syncing, then go online."}
          </p>
          <button className="primary" onClick={onExit}>
            {!application || application.verificationStatus === "rejected" ? "Open partner application" : "Back to home"} <span>→</span>
          </button>
        </div>
      )}

      {signedIn && profile?.suspended && (
        <div className="dash-card suspended">
          <h3>Account suspended</h3>
          <p>An off-app dealing violation was detected on a cancelled booking. Your subscription is cancelled and the last job amount is scheduled for deduction. Contact support to appeal.</p>
        </div>
      )}

      {signedIn && profile && !profile.suspended && (
        <>
          <div className="dash-card online-card">
            <div>
              <b>{profile.online ? "You are ONLINE" : "You are offline"}</b>
              <small>
                {myLocation ? "Live GPS locked" : "Waiting for GPS…"} · Base ₹{profile.basePrice} · {timeSlotFor(now).label}
                {timeSlotFor(now).allowance > 0 ? ` +₹${timeSlotFor(now).allowance}` : ""}
              </small>
            </div>
            <button className={profile.online ? "toggle on" : "toggle"} onClick={toggleOnline}>
              {profile.online ? "Go offline" : "Go online"}
            </button>
          </div>
          {profile.online && (
            <div className="dispatch-status">
              <p>
                <b>Keep this screen open</b> — like a cab app. SOS rings and pops on your screen until you Accept or Skip.
                You can pick any live request, not only the first one.
              </p>
              <button className="toggle" onClick={() => setMuted((value) => !value)}>{muted ? "Unmute ring" : "Mute ring"}</button>
              {notifyDenied && <small className="upi-bad">Notifications are blocked in the browser — allow them for this site, then go offline and online again.</small>}
            </div>
          )}

          <div className="dash-policy">
            {profile.verified
              ? "✓ Identity verified — live selfie, government ID and payout details are on file. Phone numbers stay hidden until both sides agree the price."
              : "Verification still syncing. Stay on this screen; you can go online once your profile shows verified."}
          </div>

          <div className="dash-policy">
            ⚠ If a booking is cancelled, do <b>not</b> travel to that location for an off-app deal. Approach after
            cancellation is detected by GPS and leads to <b>suspension, subscription cancellation and deduction of the
            job amount</b>.
          </div>

          <div className="dash-card">
            <div className="earn-head">
              <div>
                <span className="section-kicker">MY EARNINGS</span>
                <h3>₹{earnings.totalEarned.toLocaleString("en-IN")}</h3>
                <small>
                  {earnings.completedCount} visit{earnings.completedCount === 1 ? "" : "s"} completed
                  {earnings.averageTicket ? ` · avg ₹${earnings.averageTicket}` : ""}
                </small>
              </div>
              <button className="toggle" onClick={() => setHistoryOpen((open) => !open)}>
                {historyOpen ? "Hide visits" : "All visits"}
              </button>
            </div>
            <div className="earn-grid">
              <div><strong>₹{earnings.todayEarned.toLocaleString("en-IN")}</strong><span>Today · {earnings.todayCount} visits</span></div>
              <div><strong>₹{earnings.monthEarned.toLocaleString("en-IN")}</strong><span>This month · {earnings.monthCount} visits</span></div>
              <div><strong>{earnings.cancelledCount}</strong><span>Cancelled</span></div>
            </div>
            {historyOpen && (
              <div className="visit-list">
                {!history.length && <p className="dash-empty">No visits yet. Go online to start receiving requests.</p>}
                {history.map((visit) => (
                  <article key={visit.id} className={visit.status === "completed" ? "visit paid" : "visit"}>
                    <div className="visit-main">
                      <b>{visit.problem} · {visit.vehicle}</b>
                      <small>{visit.locationLabel || "Location not recorded"}</small>
                      <small>
                        {visit.atMs
                          ? new Date(visit.atMs).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
                          : "Time not recorded"}
                      </small>
                    </div>
                    <div className="visit-amount">
                      <b>{visit.price ? `₹${visit.price}` : "—"}</b>
                      <span>{STATUS_LABELS[visit.status] || visit.status}</span>
                    </div>
                  </article>
                ))}
              </div>
            )}
            <p className="earn-note">
              Customer pays you in cash or UPI. After each completed visit, send {Math.round(COMMISSION_RATE * 100)}%
              (min ₹30) to Mistry Bhai on <b>{PLATFORM_UPI}</b>. Only completed visits count as your earnings.
            </p>
            {feesDue.length > 0 && (
              <div className="pay-chip">
                <b>Pay Mistry Bhai ₹{feesDueTotal.toLocaleString("en-IN")}</b>
                <span>{feesDue.length} job fee{feesDue.length === 1 ? "" : "s"} pending</span>
              </div>
            )}
            {commissions.length > 0 && (
              <div className="visit-list">
                {commissions.slice(0, 8).map((due) => (
                  <article key={due.id} className={due.status === "collected" ? "visit paid" : "visit"}>
                    <div className="visit-main">
                      <b>Platform fee ₹{due.amount}</b>
                      <small>From job ₹{due.jobAmount} · {due.status === "due" ? "Pay now" : due.status}</small>
                    </div>
                    {due.status === "due" ? (
                      <div className="offer-actions">
                        <a className="primary" href={platformUpiLink(due.amount, `Mistry Bhai job ${due.requestId.slice(0, 6)}`)}>
                          Pay UPI ₹{due.amount}
                        </a>
                        <button
                          className="offer-reset"
                          onClick={() => void claimCommissionPaid(due.id)
                            .then(() => onToast("Marked paid — owner will confirm"))
                            .catch(() => onToast("Could not update fee"))}
                        >
                          I paid this
                        </button>
                      </div>
                    ) : (
                      <div className="visit-amount"><b>{due.status}</b></div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>

          {!job && profile.online && (
            <div className={visibleRequests.length ? "dash-card dispatch-live" : "dash-card"}>
              <h3>{visibleRequests.length ? `${visibleRequests.length} customers waiting — pick any` : "Nearby requests"}</h3>
              {!visibleRequests.length && <p className="dash-empty">Listening within 25 km… keep this tab open. New SOS will ring here until you Accept or Skip.</p>}
              <div className="offer-list">
                {visibleRequests.map((request) => {
                  const suggested = suggestQuote({
                    basePrice: profile.basePrice,
                    distanceKm: request.distanceKm,
                    at: now,
                  });
                  const edited = Number(quotes[request.id]) || 0;
                  return (
                    <article key={request.id} className="offer">
                      <div className="offer-main">
                        <b>{request.problem} · {request.vehicle}</b>
                        <small>
                          {request.distanceKm !== undefined ? `${request.distanceKm} km away` : "Distance unknown"}
                          {" · "}{minutesAgo(request.createdAtMs)}
                          {" · "}{request.locationLabel}
                        </small>
                        <span className="offer-calc">{explainQuote(suggested)}</span>
                      </div>
                      <div className="offer-actions">
                        <input
                          type="number"
                          min="49"
                          aria-label="Your quote in rupees"
                          value={quotes[request.id] ?? String(suggested.total)}
                          onChange={(e) => setQuotes({ ...quotes, [request.id]: e.target.value })}
                        />
                        <button className="primary" disabled={busy} onClick={() => accept(request)}>
                          Accept ₹{edited || suggested.total}
                        </button>
                        <button
                          className="offer-skip"
                          disabled={busy}
                          onClick={() => setSkipped((prev) => ({ ...prev, [request.id]: true }))}
                        >
                          Skip
                        </button>
                        {edited > 0 && edited !== suggested.total && (
                          <button
                            className="offer-reset"
                            onClick={() => setQuotes({ ...quotes, [request.id]: String(suggested.total) })}
                          >
                            Reset to ₹{suggested.total}
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          )}

          {job && job.status !== "cancelled" && (
            <div className="dash-card">
              {partnerFlow && (
                <JobStepGuide
                  guide={partnerFlow}
                  reach={partnerReach}
                  jobCode={job.jobCode}
                  price={job.price}
                  onPrimaryAction={
                    job.status === "completed" && job.paymentStatus !== "collected"
                      ? () => void markPaid()
                      : nextStep
                        ? () => tapJobStep(nextStep)
                        : undefined
                  }
                  primaryLabel={
                    job.status === "completed" && job.paymentStatus !== "collected"
                      ? "I received the money"
                      : nextStep === "en_route" ? "Tap: On the way"
                      : nextStep === "arrived" ? "Tap: Arrived"
                      : nextStep === "in_progress" ? "Tap: Start work"
                      : nextStep === "completed" ? "Tap: Job done"
                      : undefined
                  }
                />
              )}
              <div className="job-head">
                <div>
                  <span className="step">{job.status === "partner_accepted" ? "WAITING FOR CUSTOMER" : `${job.problem} · ${job.vehicle}`}</span>
                  <h3>₹{job.price}{partnerReach && jobConfirmed ? ` · ${partnerReach.etaLabel} · ${partnerReach.distanceLabel}` : ""}</h3>
                </div>
                {job.status === "partner_accepted" && (
                  <button
                    className="text-danger"
                    onClick={() => void partnerCancelAcceptance(job.id)
                      .then(() => onToast("Left this job — other mistris can take it"))
                      .catch(() => onToast("Could not leave this job"))}
                  >
                    Can't go
                  </button>
                )}
              </div>
              {job.status === "partner_accepted" && (
                <p className="earn-note">
                  You accepted. Wait for the customer to tap Agree. Tap <b>Can't go</b> only if you cannot reach them — the request goes back to other mistris.
                </p>
              )}

              <MapView
                you={myLocation}
                mistri={customerPoint}
                height={220}
                youLabel="YOU"
                mistriLabel="CUSTOMER"
              />

              {jobConfirmed ? (
                <>
                  {job.status === "completed" && (
                    <div className="pay-chip pay-chip-big">
                      <b>Collect ₹{job.price || 0}</b>
                      <span>{job.paymentStatus === "collected" ? "Payment received ✓" : job.paymentMethod === "cash" ? "Cash on the job" : "UPI on the job"}</span>
                      {job.paymentStatus !== "collected" ? (
                        <button className="job-flow-cta flash" onClick={() => void markPaid()}>
                          I received the money
                        </button>
                      ) : (
                        <button className="offer-reset" onClick={() => void undoPaid()}>
                          Undo — not paid yet
                        </button>
                      )}
                    </div>
                  )}
                  <div className="contact-card">
                    {customerContact
                      ? <a href={`tel:${customerContact.phone}`} className="phone-chip">📞 Customer: {customerContact.phone}</a>
                      : <span className="phone-chip pending">Customer number appears here once they share it</span>}
                    {contacts[user!.uid]
                      ? <span className="phone-chip mine">Your number is shared ✓</span>
                      : (
                        <span className="share-row">
                          <input inputMode="tel" placeholder="Your 10-digit number" value={phoneForm} onChange={(e) => setPhoneForm(e.target.value)} />
                          <button onClick={sharePhone}>Share my number</button>
                        </span>
                      )}
                  </div>
                  <div className="status-row">
                    {JOB_STEPS.map((stepItem) => {
                      const next = nextJobStep(job.status);
                      const enabled = stepItem.status === next || stepItem.status === job.status;
                      const isNext = stepItem.status === next;
                      return (
                        <button
                          key={stepItem.status}
                          className={[
                            "status-btn",
                            job.status === stepItem.status ? "active" : "",
                            isNext ? "flash" : "",
                          ].filter(Boolean).join(" ")}
                          disabled={!enabled}
                          onClick={() => tapJobStep(stepItem.status)}
                        >{stepItem.label}</button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="dash-empty">Price ₹{job.price} is locked. The customer's confirmation is the mutual agreement — numbers unlock right after.</p>
              )}

              <div className="dash-chat">
                <div className="dash-chat-log">
                  {chatMessages.map((row, index) => <p key={index} className={row.mine ? "mine" : ""}>{row.text}</p>)}
                </div>
                <form onSubmit={(e) => { e.preventDefault(); void sendChat(); }}>
                  <input placeholder="Chat (no phone/UPI/links before confirm)" value={chatText} onChange={(e) => setChatText(e.target.value)} />
                  <button type="submit">→</button>
                </form>
              </div>
            </div>
          )}

          {job && job.status === "cancelled" && (
            <div className="dash-card suspended">
              <h3>Booking cancelled by customer</h3>
              <p>
                Do not travel to this location. Your GPS stays visible for a short window — moving toward the
                cancelled location triggers automatic suspension and payment deduction. Stay online for the next
                request instead.
              </p>
            </div>
          )}
        </>
      )}

      {verifyStep && (
        <div className="verify-overlay" role="dialog" aria-modal="true">
          <div className="verify-card">
            <h3>{verifyStep === "arrived" ? "Confirm arrival" : "Start work"}</h3>
            <p>Ask the customer for their 4-digit job code from their screen.</p>
            <input
              inputMode="numeric"
              maxLength={4}
              placeholder="4-digit code"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
            />
            <div className="verify-actions">
              <button className="primary" disabled={verifyCode.length !== 4} onClick={() => void submitVerifyCode()}>
                Verify &amp; continue
              </button>
              <button className="offer-reset" onClick={() => { setVerifyStep(null); setVerifyCode(""); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
